import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranding } from '../contexts/BrandingContext';
import {
  LayoutDashboard,
  Building2,
  Settings,
  LogOut,
  ChevronDown,
  Menu,
  X,
} from 'lucide-react';
import { cn, API_BASE_URL } from '../lib/utils';
import ThemeToggle from './ThemeToggle';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/superadmin' },
  { icon: Building2, label: 'Companies', path: '/superadmin/companies' },
  { icon: Settings, label: 'Settings', path: '/superadmin/settings' },
];

export const SuperAdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { branding } = useBranding();
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  const handleLogout = () => {
    logout();
    navigate('/superadmin/login');
  };

  const closeMobileNav = () => setMobileNavOpen(false);

  const logo = branding?.logo_url ? (
    <img
      src={`${API_BASE_URL}${branding.logo_url}?v=${encodeURIComponent(String(branding.rev || 0))}`}
      alt={branding?.brand_name || 'Logo'}
      className="w-8 h-8 rounded object-contain"
    />
  ) : (
    <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
      <span className="text-white font-bold text-sm">W</span>
    </div>
  );

  const sidebar = (
    <>
      <div className="h-16 flex items-center justify-between px-4 border-b border-border gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {logo}
          <span className="font-semibold text-foreground truncate">
            {branding?.brand_name || 'WingsDiet'}
          </span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <div className="hidden md:block">
            <ThemeToggle />
          </div>
          <button
            type="button"
            className="md:hidden p-2 rounded-lg hover:bg-muted text-muted-foreground"
            onClick={closeMobileNav}
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <nav className="flex-1 py-4 px-3 sidebar overflow-y-auto">
        {navItems.map((item) => {
          const isActive =
            location.pathname === item.path ||
            (item.path !== '/superadmin' && location.pathname.startsWith(item.path));
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={closeMobileNav}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={cn(
                'nav-item flex items-center gap-3 px-3 py-2.5 rounded-md mb-1 transition-colors',
                isActive
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-sm font-medium">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-3 border-t border-border space-y-2">
        <div className="md:hidden px-1">
          <ThemeToggle className="w-full justify-center" />
        </div>
        <div className="relative">
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-muted transition-colors"
            data-testid="user-menu-btn"
          >
            <div className="w-8 h-8 rounded-full bg-primary/15 flex items-center justify-center">
              <span className="text-primary font-medium text-sm">
                {user?.name?.charAt(0) || 'S'}
              </span>
            </div>
            <div className="flex-1 text-left">
              <p className="text-sm font-medium text-foreground truncate">
                {user?.name || 'Super Admin'}
              </p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          </button>

          {showDropdown && (
            <div className="absolute bottom-full left-0 right-0 mb-1 bg-card rounded-lg border border-border shadow-lg py-1">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-foreground hover:bg-muted"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background flex overflow-hidden">
      <header className="md:hidden fixed top-0 inset-x-0 z-40 h-14 bg-card border-b border-border flex items-center justify-between px-3 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="p-2 rounded-lg hover:bg-muted text-foreground shrink-0"
            aria-label="Open menu"
          >
            <Menu className="w-5 h-5" />
          </button>
          {logo}
          <span className="text-sm font-semibold text-foreground truncate">
            {branding?.brand_name || 'WingsDiet'}
          </span>
        </div>
      </header>

      {mobileNavOpen && (
        <button
          type="button"
          className="md:hidden fixed inset-0 z-40 bg-black/50"
          aria-label="Close menu overlay"
          onClick={closeMobileNav}
        />
      )}

      <aside
        className={cn(
          'bg-card border-r border-border flex flex-col w-64 shrink-0',
          'fixed md:static inset-y-0 left-0 z-50 h-screen',
          'transition-transform duration-300 ease-in-out',
          mobileNavOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
      >
        {sidebar}
      </aside>

      <main className="flex-1 min-w-0 overflow-auto pt-14 md:pt-0">
        {children}
      </main>
    </div>
  );
};
